import { GameIcon } from "../assets/gameIcons.js";
import { Icon as FunctionalIcon } from "../ui/Icon.js";
import { useEffect } from "react";
import { buildingById } from "@zend/content";
import { pick, useT } from "../i18n/index.js";
import { Button } from "../ui/Button.js";
import { Timer } from "../ui/Timer.js";
import { onWs } from "../api/ws.js";
import { navigate } from "../router.js";
import { jumpToMap } from "./ChatView.js";
import { socialApi } from "./api.js";
import { lastSeenText, useSocial } from "./state.js";

/** Neighbours within 15 tiles and contacts: presence, assist, message, profile (R76). */
export function NeighboursView() {
  const t = useT();
  const neighbours = useSocial((s) => s.neighbours);
  const contacts = useSocial((s) => s.contacts);
  const refresh = useSocial((s) => s.refreshNeighbours);
  const openProfile = useSocial((s) => s.openProfile);
  const openDm = useSocial((s) => s.openDm);
  const run = useSocial((s) => s.run);
  useEffect(() => {
    void refresh();
    const off = onWs("presence.changed", () => void refresh());
    const poll = setInterval(() => void refresh(), 30_000);
    return () => {
      off();
      clearInterval(poll);
    };
  }, [refresh]);
  const assist = async (id: string) => {
    if (await run(() => socialApi.assist(id), t("social.neighbours.assisted"))) void refresh();
  };
  const message = async (id: string, name: string) => {
    await openDm(id, name);
    navigate("/app/chat");
  };
  return (
    <div data-testid="neighbours-view">
      <h3 style={{ padding: "0 12px", margin: "4px 0 8px" }}>{t("social.neighbours.title")}</h3>
      {!neighbours ? <p className="dim" style={{ padding: 12 }}>{t("common.loading")}</p> : null}
      {neighbours?.length === 0 ? <p className="dim" style={{ padding: 12 }}>{t("social.neighbours.none")}</p> : null}
      {neighbours?.map((n) => (
        <div key={n.accountId} className="list-row" data-testid={`neighbour-${n.accountId}`}>
          <div className="row" style={{ gap: 8 }}>
            <span className={`presence-dot ${n.online ? "on" : ""}`} title={lastSeenText(n.lastSeen)} />
            <button type="button" className="plain grow" style={{ textAlign: "left" }} onClick={() => void openProfile(n.accountId)}>
              <strong>{n.name}</strong> {n.bot ? <span className="hint">NPC</span> : null}
              <div className="hint">
                HQ {n.hq} · {t("world.distance", { n: n.distance })} · {lastSeenText(n.lastSeen)}
                {n.shielded ? <> · <GameIcon id="emote.shield" label={t("world.shielded")} /></> : null}
              </div>
            </button>
            <button type="button" className="plain mono" onClick={() => jumpToMap(n.x, n.y)}>
              {n.x},{n.y}
            </button>
          </div>
          <div className="row" style={{ gap: 6, marginTop: 6, flexWrap: "wrap" }}>
            {n.assist ? (
              <Button variant={n.assist.canHelp ? "primary" : "default"} style={{ minHeight: 30 }} disabled={!n.assist.canHelp} onClick={() => void assist(n.accountId)} data-testid="neighbour-assist">
                <FunctionalIcon name="people" /> {pick(buildingById(n.assist.buildingId).name)} · <Timer finishesAt={n.assist.finishesAt} /> · {n.assist.helpers}/{n.assist.max}
              </Button>
            ) : (
              <span className="hint">{t("social.neighbours.idle")}</span>
            )}
            <Button style={{ minHeight: 30 }} onClick={() => void message(n.accountId, n.name)} data-testid="neighbour-message">
              <FunctionalIcon name="mail" /> {t("social.profile.message")}
            </Button>
            <Button style={{ minHeight: 30 }} onClick={() => void run(() => (n.contact ? socialApi.removeContact(n.accountId) : socialApi.addContact(n.accountId))).then(() => refresh())} data-testid="neighbour-contact">
              <FunctionalIcon name={n.contact ? "check" : "plus"} /> {n.contact ? t("social.profile.contactRemove") : t("social.profile.contactAdd")}
            </Button>
          </div>
        </div>
      ))}
      <h3 style={{ padding: "0 12px", margin: "16px 0 8px" }}>{t("social.contacts.title")}</h3>
      {contacts?.length === 0 ? <p className="dim" style={{ padding: 12 }}>{t("social.contacts.none")}</p> : null}
      {contacts?.map((c) => (
        <div key={c.accountId} className="row list-row" data-testid={`contact-${c.accountId}`}>
          <span className={`presence-dot ${c.online ? "on" : ""}`} />
          <button type="button" className="plain grow" style={{ textAlign: "left" }} onClick={() => void openProfile(c.accountId)}>
            <strong>{c.name}</strong>
            <div className="hint">
              HQ {c.hq} · {lastSeenText(c.lastSeen)}
            </div>
          </button>
          {c.position ? (
            <button type="button" className="plain mono" onClick={() => jumpToMap(c.position!.x, c.position!.y)}>
              {c.position.x},{c.position.y}
            </button>
          ) : null}
          <Button style={{ minHeight: 30 }} onClick={() => void message(c.accountId, c.name)} aria-label={t("social.profile.message")}>
            <FunctionalIcon name="mail" />
          </Button>
        </div>
      ))}
    </div>
  );
}
